import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Box,
} from '@mui/material';
import Carousel from 'react-multi-carousel';
import uniqueId from 'lodash.uniqueid';

import LanguageIcon from '../professionalBackground/LanguageIcon';
import GitHubHeatmap from '../contactMe/GitHubHeatmap';
import { CalendarCard } from '../contactMe/ContactMe.styles';
import { getGitHubContributions } from '../../../utils/githubContributions';

import 'react-multi-carousel/lib/styles.css';

import { endorsements } from '../../../copy/endorsements';
import EndorsementHighlight from '../endorsements/EndorsementHighlight';
import {
  MainContainer,
  MainBox,
  HeaderStack,
  MainHeading,
  SubtitleText,
  CarouselContainer,
  MainGrid,
  MainCard,
  CardHeaderStyled,
  CardContentStyled,
  ViewAllButton,
  ReviewsImageList,
  ReviewImageListItem,
  LoadingContainer,
  ErrorAlert,
} from './MainPage.styles';

function MainPage({ goToEndorsements }) {
  const navigate = useNavigate();
  const [showArrows, toggleArrows] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contributionsData, setContributionsData] = useState(null);
  const theme = useTheme();
  const large = useMediaQuery(theme.breakpoints.up('lg'));
  const medium = useMediaQuery(theme.breakpoints.up('md'));
  const small = useMediaQuery(theme.breakpoints.up('sm'));
  const tiny = useMediaQuery(theme.breakpoints.up('xs'));

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const data = await getGitHubContributions();
        setContributionsData(data);
      } catch (err) {
        setError(err.message || 'Failed to load GitHub contributions');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 8,
      slidesToSlide: 5,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 5,
      slidesToSlide: 3,
    },
    tablet: {
      breakpoint: { max: 1024, min: 600 },
      items: 3,
      slidesToSlide: 2,
    },
    mobile: {
      breakpoint: { max: 600, min: 0 },
      items: 4,
      slidesToSlide: 1,
      partialVisibilityGutter: 0,
    },
  };

  const logosToDisplay = [
    'AWS',
    'Babel',
    'C#',
    'Canva',
    'CSS',
    'CoffeeScript',
    'Cloudinary',
    'Docker',
    'Git',
    'GitHub',
    'GraphQL',
    'HAML',
    'HTML',
    'HubSpot',
    'Java',
    'Jbuilder',
    'Javascript',
    'Jenkins',
    'Jest',
    'Jira',
    'JQuery',
    'Kubernetes',
    'MySQL',
    'Node JS',
    'PHP',
    'PostgreSQL',
    'Postman',
    'Python',
    'Rails',
    'React',
    'Redux Toolkit',
    'Redis',
    'Ruby',
    'Sass',
    'Slack',
    'Stripe API',
    'VS Code',
    'Webpack',
    'Zapier',
  ];

  const responsiveColumnNumber = () => {
    if (large) {
      return 3;
    } else if (medium) {
      return 2;
    } else if (small) {
      return 1;
    } else if (tiny) {
      return 1;
    }
    return 3;
  };

  return (
    <MainContainer>
      <MainBox>
        <HeaderStack>
          <MainHeading variant="h2" component="h2">
            From Education to Engineering
          </MainHeading>
          <SubtitleText variant="subtitle1">
            Making complex things simple, and simple things beautiful
          </SubtitleText>
        </HeaderStack>
        <CarouselContainer
          onMouseEnter={() => {
            toggleArrows(true);
          }}
          onMouseLeave={() => {
            toggleArrows(false);
          }}
        >
          <Carousel
            autoPlay={true}
            autoPlaySpeed={4000}
            centerMode={false}
            partialVisible={true}
            containerClass="carousel-container"
            infinite
            itemClass="carouselItem"
            responsive={responsive}
            arrows={showArrows}
            keyBoardControl={true}
            draggable
            swipeable
            customTransition="transform 4s ease-in-out"
            transitionDuration="4s"
            removeArrowOnDeviceType={['tablet', 'mobile']}
            dotListClass="custom-dot-list-style"
            aria-label="Technology logos carousel"
          >
            {logosToDisplay.map(logo => {
              return (
                <LanguageIcon key={uniqueId(logo)} name={logo} size="auto" />
              );
            })}
          </Carousel>
        </CarouselContainer>

        <CalendarCard
          sx={{
            marginBottom: '16px',
            width: '100%',
            maxWidth: '100%',
            flex: 'none',
            boxSizing: 'border-box',
          }}
        >
          <Typography component="h2" variant="h5">
            GitHub Activity
          </Typography>
          {loading && (
            <LoadingContainer
              role="status"
              aria-live="polite"
              aria-label="Loading GitHub contributions"
            >
              <CircularProgress aria-hidden="true" />
              <span className="sr-only">Loading GitHub contributions...</span>
            </LoadingContainer>
          )}
          {error && (
            <ErrorAlert severity="error" role="alert" aria-live="assertive">
              {error}
            </ErrorAlert>
          )}
          {!loading && !error && contributionsData && (
            <Box sx={{ marginTop: '16px' }}>
              <GitHubHeatmap contributions={contributionsData.contributions} />
            </Box>
          )}
        </CalendarCard>

        <MainGrid container spacing={{ xs: 0.5, sm: 1, md: 1 }}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <MainCard>
              <CardHeaderStyled
                title={<Typography variant="h5">Kind Words</Typography>}
                subheader={
                  <Typography variant="subtitle1">
                    What colleagues have to say
                  </Typography>
                }
                action={
                  (large || medium || small) && (
                    <ViewAllButton
                      variant="text"
                      onClick={() => {
                        if (goToEndorsements) {
                          goToEndorsements();
                        } else {
                          navigate('/endorsements');
                        }
                      }}
                    >
                      Read the full endorsements
                    </ViewAllButton>
                  )
                }
              />
              <CardContentStyled>
                <ReviewsImageList
                  variant="masonry"
                  cols={responsiveColumnNumber()}
                  gap={8}
                >
                  {endorsements.map(endorsement => (
                    <ReviewImageListItem
                      key={`${endorsement.reviewer}-highlight`}
                    >
                      <EndorsementHighlight endorsement={endorsement} />
                    </ReviewImageListItem>
                  ))}
                </ReviewsImageList>
              </CardContentStyled>
            </MainCard>
          </Grid>
        </MainGrid>
      </MainBox>
    </MainContainer>
  );
}

export default MainPage;
